import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CategoriesService } from '../service/categories.service';
import * as CategoryActions from './categories.actions';
import { FilterEntity, FilterValueEntity } from '@frontend/data-access-documents';
import { CategoryFilterApiValue } from './categories.models';
import { of, catchError, filter, map, switchMap, take } from 'rxjs';
import { AuthService } from '@frontend/util-client-auth';
import { CLIENT_DATA, ClientData } from '@frontend/util-api-data';

function mapCategoryFilterValues(values: CategoryFilterApiValue[]): FilterValueEntity[] {
  return values.map((v) => ({
    id: v.id,
    value: v.value ?? v.title ?? '',
    title: v.title,
    count: v.count,
    active: v.active,
    children: v.children ? mapCategoryFilterValues(v.children) : undefined,
  }));
}

@Injectable()
export class CategoriesEffects {
  private actions$ = inject(Actions);
  private categoriesService = inject(CategoriesService);
  private authService = inject(AuthService);
  private clientData = inject<ClientData>(CLIENT_DATA);

  getCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.categoryPageOpened),
      switchMap((action) => {
        // only schueco uses the keycloak client where we need to wait for init
        if (this.clientData.name !== 'schueco') {
          return of(action);
        }
        return this.authService.isInitialized$.pipe(
          filter((value) => value),
          take(1),
          map((_) => action),
        );
      }),
      switchMap((action) => {
        return this.categoriesService.getCategories(action.id, action.language).pipe(
          map((_response) => CategoryActions.loadCategoriesSuccess({ categories: _response })),
          catchError((error) => of(CategoryActions.loadCategoriesFailure({ error }))),
        );
      }),
    ),
  );

  getCategoryFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoryActions.loadCategoryFilters),
      switchMap((action) => {
        return this.categoriesService.getCategoryFilters(action.language, action.id).pipe(
          map((response) => {
            const filters: FilterEntity[] = Object.entries(response.filters).map(([key, f]) => ({
              id: f.id ?? +key,
              label: f.label,
              type: f.type,
              open: f.open,
              values: mapCategoryFilterValues(f.values),
            }));
            return CategoryActions.loadCategoryFiltersSuccess({ filters });
          }),
          catchError((error) => of(CategoryActions.loadCategoryFiltersFailure({ error }))),
        );
      }),
    ),
  );
}
